/*
 *
 * Hedera Stablecoin SDK
 *
 * Copyright (C) 2023 Hedera Hashgraph, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { ContractId as HContractId } from '@hashgraph/sdk';
import { CommandHandler } from '../../../../../core/decorator/CommandHandlerDecorator.js';
import { lazyInject } from '../../../../../core/decorator/LazyInjectDecorator.js';
import ContractId from '../../../../../domain/context/contract/ContractId.js';
import { StableCoin } from '../../../../../domain/context/stablecoin/StableCoin.js';
import AccountService from '../../../../service/AccountService.js';
import TransactionService from '../../../../service/TransactionService.js';
import NetworkService from '../../../../service/NetworkService.js';
import { OperationNotAllowed } from '../error/OperationNotAllowed.js';
import { CreateCommand, CreateCommandResponse } from './CreateCommand.js';
import { RESERVE_DECIMALS } from '../../../../../domain/context/reserve/Reserve.js';
import { InvalidRequest } from '../error/InvalidRequest.js';
import { EVM_ZERO_ADDRESS } from '../../../../../core/Constants.js';
import { MirrorNodeAdapter } from '../../../../../port/out/mirror/MirrorNodeAdapter.js';
import { RPCQueryAdapter } from '../../../../../port/out/rpc/RPCQueryAdapter.js';
import BigDecimal from '../../../../../domain/context/shared/BigDecimal.js';
import EvmAddress from '../../../../../domain/context/contract/EvmAddress.js';
let CreateCommandHandler = class CreateCommandHandler {
    constructor(accountService, transactionService, networkService, mirrorNodeAdapter, queryAdapter) {
        this.accountService = accountService;
        this.transactionService = transactionService;
        this.networkService = networkService;
        this.mirrorNodeAdapter = mirrorNodeAdapter;
        this.queryAdapter = queryAdapter;
    }
    async execute(command) {
        const { factory, hederaTokenManager, coin, reserveAddress, reserveInitialAmount, createReserve, proxyAdminOwnerAccount, } = command;
        if (!factory) {
            throw new InvalidRequest('Factory not found in request');
        }
        if (!hederaTokenManager) {
            throw new InvalidRequest('HederaTokenManager not found in request');
        }
        const handler = this.transactionService.getHandler();
        if (coin.maxSupply &&
            coin.initialSupply &&
            coin.initialSupply.isGreaterThan(coin.maxSupply)) {
            throw new OperationNotAllowed('Initial supply cannot be more than the max supply');
        }
        const commonDecimals = RESERVE_DECIMALS > coin.decimals ? RESERVE_DECIMALS : coin.decimals;
        if (coin.initialSupply) {
            if (createReserve &&
                reserveInitialAmount &&
                coin.initialSupply
                    .setDecimals(commonDecimals)
                    .isGreaterThan(reserveInitialAmount.setDecimals(commonDecimals))) {
                throw new OperationNotAllowed('Initial supply cannot be more than the reserve initial amount');
            }
            else if (reserveAddress) {
                const reserveContractEvmAddress = (await this.mirrorNodeAdapter.getContractInfo(reserveAddress.value)).evmAddress;
                const reserveAmount = BigDecimal.fromStringFixed((await this.queryAdapter.getReserveLatestRoundData(new EvmAddress(reserveContractEvmAddress)))[1].toString(), RESERVE_DECIMALS);
                if (coin.initialSupply
                    .setDecimals(commonDecimals)
                    .isGreaterThan(reserveAmount.setDecimals(commonDecimals))) {
                    throw new OperationNotAllowed('Initial supply cannot be more than the reserve initial amount');
                }
            }
        }
        const res = await handler.create(new StableCoin(coin), factory, hederaTokenManager, createReserve, reserveAddress, reserveInitialAmount, proxyAdminOwnerAccount);
        try {
            return Promise.resolve(new CreateCommandResponse(ContractId.fromHederaContractId(HContractId.fromSolidityAddress(res.response[0][3])), res.response[0][4] === EVM_ZERO_ADDRESS
                ? new ContractId('0.0.0')
                : ContractId.fromHederaContractId(HContractId.fromString((await this.mirrorNodeAdapter.getContractInfo(res.response[0][4])).id)), res.response[0][5] === EVM_ZERO_ADDRESS
                ? new ContractId('0.0.0')
                : ContractId.fromHederaContractId(HContractId.fromString((await this.mirrorNodeAdapter.getContractInfo(res.response[0][5])).id))));
        }
        catch (e) {
            if (res.response == 1)
                return Promise.resolve(new CreateCommandResponse(new ContractId('0.0.0'), new ContractId('0.0.0'), new ContractId('0.0.0')));
            else
                throw e;
        }
    }
};
CreateCommandHandler = __decorate([
    CommandHandler(CreateCommand),
    __param(0, lazyInject(AccountService)),
    __param(1, lazyInject(TransactionService)),
    __param(2, lazyInject(NetworkService)),
    __param(3, lazyInject(MirrorNodeAdapter)),
    __param(4, lazyInject(RPCQueryAdapter)),
    __metadata("design:paramtypes", [AccountService,
        TransactionService,
        NetworkService,
        MirrorNodeAdapter,
        RPCQueryAdapter])
], CreateCommandHandler);
export { CreateCommandHandler };
